import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, Check, Diamond, LogOut, RefreshCw, ShieldCheck, X } from "lucide-react";
import { api } from "@/api";
import { useAuth } from "@/auth";
import { VerifiedBadge } from "@/Verify";
import { ExhibitionLiveStatus } from "@/video";
import {ReportSupport} from "@/communications";
import AdminContent from "@/AdminContent";
import HomepageMediaAdmin from "@/HomepageMediaAdmin";
import {AdminCommercial} from "@/commercial";
import PackageAccess from "@/PackageAccess";
import {exhibitorApplicationShowLabel} from "@/exhibitorShows";

// Passcode gate removed — access to /admin is enforced by ProtectedRoute at
// the route level (only users with role === "admin" reach this component).

function useAdminData() {
  const [state, setState] = useState({
    summary: null, exhibitors: [], applications: [], products: [],
    exhibitions: [], buyers: [], inquiries: [], meetings: [], users: [],
    verifications: [],
    loading: true, error: "",
  });
  const load = async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const [summary, exhibitors, applications, products, exhibitions, buyers, inquiries, meetings, users, verifications] =
        await Promise.all([
          api.adminSummary(),
          api.listExhibitors({ status: "all" }),
          api.listApplications(),
          api.listProducts(),
          api.listExhibitions(),
          api.listBuyerRegistrations(),
          api.listInquiries(),
          api.listMeetings(),
          api.listUsers(),
          api.listVerifications(),
        ]);
      setState({
        summary, exhibitors, applications, products, exhibitions, buyers, inquiries, meetings, users, verifications,
        verifiedEmails: new Set(
          verifications
            .filter((v) => v.status === "verified")
            .flatMap((v) => [v.user_email, v.business_email].filter(Boolean))
            .map((e) => String(e).trim().toLowerCase())
        ),
        loading: false, error: "",
      });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err.message || "Failed to load admin data." }));
    }
  };
  useEffect(() => { load(); }, []);
  return { ...state, refresh: load };
}

function Metric({ label, value, testId }) {
  return (
    <div className="admin-metric" data-testid={testId}>
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    approved: "ok", pending: "warn", rejected: "danger",
    new: "ok", requested: "warn", read: "info", responded: "ok",
    Live: "ok", Upcoming: "info", Completed: "muted",
    verified: "ok", more_info: "info", not_applied: "muted",
  };
  const tone = map[status] || "muted";
  return <span className={`admin-pill tone-${tone}`}>{status}</span>;
}

function Row({ children, testId }) {
  return <div className="admin-row" data-testid={testId}>{children}</div>;
}

function Actions({ current, onChange, itemId, prefix }) {
  const disabled = (status) => current === status;
  return (
    <div className="admin-actions">
      <button
        className="admin-btn approve"
        disabled={disabled("approved")}
        onClick={() => onChange(itemId, "approved")}
        data-testid={`${prefix}-${itemId}-approve`}
      >
        <Check size={13}/> Approve
      </button>
      <button
        className="admin-btn reject"
        disabled={disabled("rejected")}
        onClick={() => onChange(itemId, "rejected")}
        data-testid={`${prefix}-${itemId}-reject`}
      >
        <X size={13}/> Reject
      </button>
    </div>
  );
}

// ---------- Tab views ----------

function OverviewTab({ data, onGoto }) {
  const s = data.summary || {};
  return (
    <>
      <div className="admin-metrics">
        <Metric testId="metric-exhibitors" label="Exhibitors" value={s.exhibitors}/>
        <Metric testId="metric-pending-exhibitors" label="Pending exhibitors" value={s.exhibitors_pending}/>
        <Metric testId="metric-applications" label="Applications pending" value={s.applications_pending}/>
        <Metric testId="metric-products" label="Products" value={s.products}/>
        <Metric testId="metric-exhibitions" label="Exhibitions" value={s.exhibitions}/>
        <Metric testId="metric-buyers" label="Buyer registrations" value={s.buyer_registrations}/>
        <Metric testId="metric-inquiries" label="Inquiries" value={s.inquiries}/>
        <Metric testId="metric-meetings" label="Meeting requests" value={s.meetings}/>
      </div>
      <div className="admin-overview-panels">
        <button className="admin-panel" onClick={() => onGoto("applications")} data-testid="overview-open-applications">
          <div><div className="eyebrow">TO REVIEW</div><h3>Exhibitor applications</h3></div>
          <strong>{s.applications_pending ?? 0}</strong>
        </button>
        <button className="admin-panel" onClick={() => onGoto("inquiries")} data-testid="overview-open-inquiries">
          <div><div className="eyebrow">BUYER SIGNALS</div><h3>Inquiries</h3></div>
          <strong>{s.inquiries ?? 0}</strong>
        </button>
        <button className="admin-panel" onClick={() => onGoto("meetings")} data-testid="overview-open-meetings">
          <div><div className="eyebrow">CALENDAR</div><h3>Meeting requests</h3></div>
          <strong>{s.meetings ?? 0}</strong>
        </button>
      </div>
    </>
  );
}

function ApprovalDialog({ application, data, onApprove, onClose }) {
  const dialog = useRef(null);
  const [boothId, setBoothId] = useState(application.exhibitor_id || "");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { dialog.current?.showModal(); }, []);
  const showLabel = exhibitorApplicationShowLabel(application.show_id, data.exhibitions);
  const selected = data.exhibitors.find((item) => item.id === boothId);
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    try {
      await onApprove(application.id, "approved", {
        exhibitor_id: boothId || null, country, city, confirm_company_match: confirmed,
      });
      onClose();
    } catch (err) { setError(err.message || "Approval could not be completed. Refresh and retry."); }
    finally { setBusy(false); }
  };
  return <dialog ref={dialog} onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}
    aria-labelledby="approval-title" data-testid="approval-dialog"
    style={{ maxWidth: 560, width: "calc(100% - 32px)", border: "1px solid #ddd", padding: 24 }}>
    <form onSubmit={submit} className="apply-form">
      <h2 id="approval-title">Approve and set up booth</h2>
      <p>{application.company} · {application.email}</p>
      <p data-testid="approval-exhibition">Exhibition: {showLabel}</p>
      <label>Company booth<select value={boothId} onChange={(event) => { setBoothId(event.target.value); setConfirmed(false); }} disabled={busy} data-testid="approval-booth">
        <option value="">Find a reliable match, or create if none exists</option>
        {data.exhibitors.filter((item) => item.status === "approved").map((item) =>
          <option key={item.id} value={item.id}>{item.name} · {item.email || "No contact email"} · {item.id}</option>)}
      </select></label>
      {boothId && <label><input type="checkbox" required checked={confirmed} disabled={busy}
        onChange={(event) => setConfirmed(event.target.checked)} data-testid="approval-confirm-company"/>
        I have checked that this application belongs to {selected?.name || "the selected booth"}.</label>}
      <p>Existing booth profiles, plans, products and meetings are preserved. For a new booth, enter its actual location.</p>
      <label>Country (new booth only)<input value={country} maxLength={100} disabled={busy || !!boothId}
        onChange={(event) => setCountry(event.target.value)} data-testid="approval-country"/></label>
      <label>City (new booth only)<input value={city} maxLength={100} disabled={busy || !!boothId}
        onChange={(event) => setCity(event.target.value)} data-testid="approval-city"/></label>
      {error && <p role="alert" className="admin-error">{error}</p>}
      <div className="admin-actions">
        <button type="submit" className="admin-btn approve" disabled={busy} data-testid="approval-submit">{busy ? "Setting up…" : "Approve and link booth"}</button>
        <button type="button" className="admin-btn" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </form>
  </dialog>;
}

function ApplicationReviewDialog({ application, data, onClose }) {
  const categories = application.categories?.length ? application.categories : (application.category ? [application.category] : []);
  const showLabel = exhibitorApplicationShowLabel(application.show_id, data.exhibitions);
  const proofLabel = application.proof_status === "ready" ? "Uploaded and available" : application.proof_status === "failed" ? "Upload failed — follow up with applicant" : "Not provided";
  return <div className="admin-modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="admin-modal application-review-modal" role="dialog" aria-modal="true" aria-labelledby="application-review-title" data-testid="application-review-dialog">
      <div className="admin-modal-head"><div><div className="eyebrow">EXHIBITOR APPLICATION</div><h2 id="application-review-title">{application.company}</h2><p className="admin-modal-sub">Submitted for {showLabel}</p></div><button className="icon-button" aria-label="Close application review" onClick={onClose}><X size={18}/></button></div>
      <div className="application-review-context"><div><span>Selected exhibition</span><strong>{showLabel}</strong></div><div><span>Selected package</span><strong>{application.plan || "Not provided"}</strong></div></div>
      <div className="application-review-section"><h3>Company details</h3><div className="admin-modal-grid">
        <div className="kv"><span>Country</span><strong>{application.country || "Not provided"}</strong></div>
        <div className="kv"><span>Employee count</span><strong>{application.employee_count || "Not provided"}</strong></div>
        <div className="kv full"><span>Company address</span><strong>{application.address || "Not provided"}</strong></div>
        <div className="kv full"><span>Categories</span><strong>{categories.length ? categories.join(" · ") : "Not provided"}</strong></div>
        <div className="kv full"><span>Company description</span><strong>{application.description || "Not provided"}</strong></div>
      </div></div>
      <div className="application-review-section"><h3>Contact and online presence</h3><div className="admin-modal-grid">
        <div className="kv"><span>Business email</span><strong>{application.email}</strong></div>
        <div className="kv"><span>Phone / WhatsApp</span><strong>{application.phone || "Not provided"}</strong></div>
        <div className="kv"><span>Website</span><strong>{application.website ? <a href={application.website} target="_blank" rel="noreferrer">Open website</a> : "Not provided"}</strong></div>
        <div className="kv"><span>Social / company page</span><strong>{application.social_url ? <a href={application.social_url} target="_blank" rel="noreferrer">Open page</a> : "Not provided"}</strong></div>
      </div></div>
      <div className={`application-review-proof ${application.proof_status === "ready" ? "ready" : ""}`} data-testid="application-review-proof"><div><span>Verification / proof</span><strong>{proofLabel}</strong>{application.proof_filename&&<small>{application.proof_filename}{application.proof_size ? ` · ${(application.proof_size/1024/1024).toFixed(1)} MB` : ""}</small>}</div>{application.proof_status === "ready"&&<a className="admin-btn" href={api.applicationProofUrl(application.id)} target="_blank" rel="noreferrer">Download proof</a>}</div>
      <div className="admin-modal-foot"><span><StatusPill status={application.status}/></span><button className="admin-btn" onClick={onClose}>Close</button></div>
    </section>
  </div>;
}

function ApplicationsTab({ data, onStatus }) {
  const items = data.applications;
  const [approving, setApproving] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  return (
    <><div className="admin-table" data-testid="admin-applications-table">
      <div className="admin-table-head">
        <span>Company</span><span>Email</span><span>Category</span><span>Plan</span><span>Status</span><span>Actions</span>
      </div>
      {items.length === 0 && <div className="admin-empty">No applications yet.</div>}
      {items.map((a) => (
        <Row key={a.id} testId={`application-row-${a.id}`}>
          <span><strong>{a.company}</strong><br/><small data-testid={`application-${a.id}-exhibition`}>{exhibitorApplicationShowLabel(a.show_id, data.exhibitions)}</small></span>
          <span>{a.email}{a.phone&&<><br/><small>{a.phone}</small></>}</span>
          <span>{a.categories?.length ? a.categories.join(", ") : (a.category || "—")}</span>
          <span>{a.plan || "—"}</span>
          <span><StatusPill status={a.status}/></span>
          <span className="application-admin-actions"><button className="admin-btn" onClick={() => setReviewing(a)} data-testid={`application-${a.id}-review`}>View application</button><Actions current={a.status} onChange={(id, status) => status === "approved" ? setApproving(a) : onStatus(id, status)} itemId={a.id} prefix="application"/>
            {a.status === "approved" && <button className="admin-btn" onClick={() => setApproving(a)} data-testid={`application-${a.id}-repair`}>Check / repair booth setup</button>}
          </span>
        </Row>
      ))}
    </div>{reviewing&&<ApplicationReviewDialog application={reviewing} data={data} onClose={()=>setReviewing(null)}/>} {approving && <ApprovalDialog key={approving.id} application={approving} data={data} onApprove={onStatus} onClose={() => setApproving(null)}/>}</>
  );
}

function ExhibitorsTab({ data, onStatus }) {
  const items = data.exhibitors;
  return (
    <div className="admin-table" data-testid="admin-exhibitors-table">
      <div className="admin-table-head">
        <span>Company</span><span>Country</span><span>City</span><span>Specialty</span><span>Status</span><span>Actions</span>
      </div>
      {items.length === 0 && <div className="admin-empty">No exhibitors.</div>}
      {items.map((e) => (
        <Row key={e.id} testId={`exhibitor-row-${e.id}`}>
          <span><strong>{e.name}</strong>{e.verified && <ShieldCheck size={12} style={{ marginLeft: 6, color: "#638d85" }}/>}</span>
          <span>{e.country}</span>
          <span>{e.city}</span>
          <span>{e.specialty}</span>
          <span><StatusPill status={e.status}/></span>
          <Actions current={e.status} onChange={onStatus} itemId={e.id} prefix="exhibitor"/>
          <div style={{gridColumn:'1 / -1'}}><PackageAccess exhibitor={e} exhibitions={data.exhibitions}/></div>
        </Row>
      ))}
    </div>
  );
}

function ProductsTab({ data }) {
  const items = data.products;
  return (
    <div className="admin-table" data-testid="admin-products-table">
      <div className="admin-table-head five-col">
        <span>Product</span><span>Type</span><span>Exhibitor</span><span>Detail</span><span>Added</span>
      </div>
      {items.map((p) => (
        <div className="admin-row five-col" key={p.id} data-testid={`product-row-${p.id}`}>
          <span><strong>{p.name}</strong></span>
          <span>{p.type}</span>
          <span>{p.exhibitor}</span>
          <span className="muted">{p.detail}</span>
          <span className="muted">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</span>
        </div>
      ))}
    </div>
  );
}

function LiveScheduleEditor({exhibition, onSaved}) {
  const [draft, setDraft] = useState(null), [error, setError] = useState(''), [saving, setSaving] = useState(false);
  const fields = [['live_start_date','Start date','date'],['live_end_date','End date','date'],['live_opens_at','Daily opening','time'],['live_closes_at','Daily closing','time'],['live_timezone','Exhibition timezone (IANA)','text']];
  const save = async event => {
    event.preventDefault();setSaving(true);setError('');
    try { await api.updateLiveSchedule(exhibition.id, draft);setDraft(null);await onSaved(); }
    catch (err) { setError(err.message || 'Could not save schedule'); }
    finally { setSaving(false); }
  };
  return <div data-testid={`schedule-editor-${exhibition.id}`}>
    <ExhibitionLiveStatus exhibition={exhibition}/>
    {!draft ? <button className="product-btn" onClick={() => setDraft(Object.fromEntries([...fields.map(([key]) => [key,exhibition[key] || '']),['live_schedule_revision',exhibition.live_schedule_revision || 0]]))}>Edit LIVE schedule</button>
      : <form onSubmit={save}>
        {fields.map(([key,label,type]) => <label key={key} style={{display:'block'}}>{label}<input required type={type} value={draft[key]} data-testid={`schedule-${key}`} onChange={event => setDraft(previous => ({...previous,[key]:event.target.value}))}/></label>)}
        <p>Daily hours use the exhibition timezone. Saving may close active public broadcasts. Private meetings are unaffected.</p>
        {error && <p role="alert">{error}</p>}
        <button className="product-btn" disabled={saving} type="submit">{saving ? 'Saving…' : 'Save schedule'}</button>
        <button className="product-btn" disabled={saving} type="button" onClick={() => setDraft(null)}>Cancel</button>
      </form>}
  </div>;
}

function ExhibitionsTab({ data }) {
  const items = data.exhibitions;
  return (
    <div className="admin-table" data-testid="admin-exhibitions-table">
      <div className="admin-table-head five-col">
        <span>Exhibition</span><span>Edition</span><span>Dates</span><span>Category</span><span>Status</span>
      </div>
      {items.map((e) => (
        <div className="admin-row five-col" key={e.id} data-testid={`exhibition-row-${e.id}`}>
          <span><strong>{e.short}</strong></span>
          <span>{e.edition}</span>
          <span>{e.dates}</span>
          <span>{e.category}</span>
          <div><StatusPill status={e.status}/><LiveScheduleEditor key={`${e.id}:${e.live_schedule_revision}`} exhibition={e} onSaved={data.refresh}/></div>
        </div>
      ))}
    </div>
  );
}

function BuyersTab({ data }) {
  const items = data.buyers;
  return (
    <div className="admin-table" data-testid="admin-buyers-table">
      <div className="admin-table-head five-col">
        <span>Name</span><span>Email</span><span>Company</span><span>Country</span><span>Registered</span>
      </div>
      {items.length === 0 && <div className="admin-empty">No buyer registrations yet.</div>}
      {items.map((b) => (
        <div className="admin-row five-col" key={b.id} data-testid={`buyer-row-${b.id}`}>
          <span><strong>{b.name}</strong></span>
          <span>{b.email}</span>
          <span>{b.company || "—"}</span>
          <span>{b.country || "—"}</span>
          <span className="muted">{new Date(b.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function InquiriesTab({ data }) {
  const items = data.inquiries;
  const exhibitorLookup = useMemo(
    () => Object.fromEntries((data.exhibitors || []).map((e) => [e.id, e.name])),
    [data.exhibitors]
  );
  const verifiedEmails = data.verifiedEmails;
  const isVerified = (email) => email && verifiedEmails?.has(String(email).trim().toLowerCase());
  return (
    <div className="admin-table" data-testid="admin-inquiries-table">
      <div className="admin-table-head five-col">
        <span>Buyer</span><span>Email</span><span>Exhibitor</span><span>Message</span><span>Received</span>
      </div>
      {items.length === 0 && <div className="admin-empty">No inquiries yet.</div>}
      {items.map((i) => (
        <div className="admin-row five-col" key={i.id} data-testid={`inquiry-row-${i.id}`}>
          <span>
            <strong>{i.buyer_name}</strong>
            {isVerified(i.buyer_email) && <>&nbsp;<VerifiedBadge testId={`admin-inquiry-${i.id}-verified-badge`}/></>}
          </span>
          <span>{i.buyer_email}</span>
          <span>{exhibitorLookup[i.exhibitor_id] || i.exhibitor_id}</span>
          <span className="muted">{i.message}</span>
          <span className="muted">{new Date(i.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function MeetingsTab({ data }) {
  const meetingTime = (value) => {
    if (!value) return "Time pending";
    const normalized = /^\d{4}-\d\d-\d\dT/.test(value) && !/(Z|[+-]\d\d:\d\d)$/i.test(value) ? value + "Z" : value;
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? "Time unavailable" : date.toLocaleString(undefined, {timeZoneName:"short"});
  };
  const items = data.meetings;
  const exhibitorLookup = useMemo(
    () => Object.fromEntries((data.exhibitors || []).map((e) => [e.id, e.name])),
    [data.exhibitors]
  );
  const verifiedEmails = data.verifiedEmails;
  const isVerified = (email) => email && verifiedEmails?.has(String(email).trim().toLowerCase());
  return (
    <div className="admin-table" data-testid="admin-meetings-table">
      <div className="admin-table-head five-col">
        <span>Requester</span><span>Email</span><span>Exhibitor</span><span>Status / time</span><span>Requested at</span>
      </div>
      {items.length === 0 && <div className="admin-empty">No meeting requests yet.</div>}
      {items.map((m) => (
        <div className="admin-row five-col" key={m.id} data-testid={`meeting-row-${m.id}`}>
          <span>
            <strong>{m.requester_name}</strong>
            {isVerified(m.requester_email) && <>&nbsp;<VerifiedBadge testId={`admin-meeting-${m.id}-verified-badge`}/></>}
          </span>
          <span>{m.requester_email}</span>
          <span>{exhibitorLookup[m.exhibitor_id] || m.exhibitor_id}</span>
          <span className="muted">
            <strong>{m.status}</strong><br/>
            {m.confirmed_time ? `Confirmed: ${meetingTime(m.confirmed_time)}` : m.requested_time ? `Proposed: ${meetingTime(m.requested_time)}` : "Time pending"}
            {!!m.history?.length && <details><summary>Meeting history</summary>{m.history.map((line,index) => <p key={index}>{line}</p>)}</details>}
          </span>
          <span className="muted">{new Date(m.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function UsersTab({ data, onRoleChange, exhibitors }) {
  const items = data.users;
  return (
    <div className="admin-table" data-testid="admin-users-table">
      <div className="admin-table-head six-col">
        <span>Name</span><span>Email</span><span>Role</span><span>Linked exhibitor</span><span>Signed up</span><span>Set role</span>
      </div>
      {items.length === 0 && <div className="admin-empty">No users have signed in yet.</div>}
      {items.map((u) => (
        <div className="admin-row six-col" key={u.user_id} data-testid={`user-row-${u.user_id}`}>
          <span><strong>{u.name}</strong></span>
          <span>{u.email}</span>
          <span><StatusPill status={u.role}/></span>
          <span className="muted">{u.exhibitor_id || "—"}</span>
          <span className="muted">{new Date(u.created_at).toLocaleDateString()}</span>
          <span className="admin-role-controls">
            <select
              className="admin-role-select"
              value={u.role}
              onChange={(e) => onRoleChange(u.user_id, { role: e.target.value, exhibitor_id: u.exhibitor_id })}
              data-testid={`user-${u.user_id}-role-select`}
            >
              <option value="buyer">buyer</option>
              <option value="exhibitor">exhibitor</option>
              <option value="admin">admin</option>
            </select>
            {u.role === "exhibitor" && (
              <select
                className="admin-role-select"
                value={u.exhibitor_id || ""}
                onChange={(e) => onRoleChange(u.user_id, { role: "exhibitor", exhibitor_id: e.target.value || null })}
                data-testid={`user-${u.user_id}-exhibitor-select`}
              >
                <option value="">— link exhibitor —</option>
                {exhibitors.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------- Buyer Verification ----------

const VSTATUS_LABEL = {
  pending: "Pending review",
  more_info: "More info requested",
  verified: "Verified",
  rejected: "Rejected",
};

function VerificationsTab({ data, onDecide }) {
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("pending");
  const items = useMemo(() => {
    if (filter === "all") return data.verifications;
    return data.verifications.filter((v) => v.status === filter);
  }, [data.verifications, filter]);
  const opened = items.find((v) => v.id === openId) || data.verifications.find((v) => v.id === openId);

  return (
    <div>
      <div className="verify-filter-bar" data-testid="verify-filter-bar">
        {["pending", "more_info", "verified", "rejected", "all"].map((k) => (
          <button
            key={k}
            className={filter === k ? "verify-filter on" : "verify-filter"}
            onClick={() => setFilter(k)}
            data-testid={`verify-filter-${k}`}
          >
            {k === "all" ? "All" : VSTATUS_LABEL[k]}
          </button>
        ))}
      </div>
      <div className="admin-table" data-testid="admin-verifications-table">
        <div className="admin-table-head verify-cols">
          <span>Buyer</span><span>Company</span><span>Country</span><span>Buyer Type</span><span>Applied</span><span>Status</span><span>Actions</span>
        </div>
        {items.length === 0 && <div className="admin-empty" data-testid="admin-verifications-empty">No applications in this view.</div>}
        {items.map((v) => (
          <div className="admin-row verify-cols" key={v.id} data-testid={`verification-row-${v.id}`}>
            <span>
              <strong>{v.full_name}</strong>
              {v.status === "verified" && <BadgeCheck size={13} style={{ marginLeft: 5, color: "#638d85", verticalAlign: "middle" }}/>}
              <div className="muted" style={{ fontSize: 10, marginTop: 3 }}>{v.business_email}</div>
            </span>
            <span>{v.company_name}</span>
            <span>{v.country}</span>
            <span>{v.buyer_type}</span>
            <span className="muted">{v.submitted_at ? new Date(v.submitted_at).toLocaleDateString() : "—"}</span>
            <span><StatusPill status={v.status}/></span>
            <span>
              <button className="admin-btn" onClick={() => setOpenId(v.id)} data-testid={`verification-${v.id}-open`}>
                <ArrowUpRight size={12}/> Open
              </button>
            </span>
          </div>
        ))}
      </div>
      {opened && (
        <VerificationModal
          verification={opened}
          onClose={() => setOpenId(null)}
          onDecide={onDecide}
        />
      )}
    </div>
  );
}

function VerificationModal({ verification: v, onClose, onDecide }) {
  const [notes, setNotes] = useState(v.admin_notes || "");
  const [busy, setBusy] = useState(false);

  const decide = async (action) => {
    setBusy(true);
    try { await onDecide(v.id, action, notes); onClose(); }
    finally { setBusy(false); }
  };

  const domainNote = v.public_email_domain
    ? "Business email uses a public provider — additional manual review recommended."
    : v.domain_match
      ? "Business email domain matches the company website domain."
      : "Business email domain does not match the company website. Manual review recommended.";
  const domainTone = v.public_email_domain ? "warn" : v.domain_match ? "ok" : "warn";

  return (
    <div className="admin-modal-backdrop" onClick={onClose} data-testid="verification-modal-backdrop">
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" data-testid="verification-modal">
        <header className="admin-modal-head">
          <div>
            <div className="eyebrow">VERIFIED BUYER APPLICATION</div>
            <h2 data-testid="verification-modal-name">{v.full_name}</h2>
            <p className="admin-modal-sub">{v.job_title} · {v.company_name}</p>
          </div>
          <StatusPill status={v.status}/>
        </header>

        <div className={`verify-flag verify-flag-${domainTone}`} data-testid="verification-domain-flag">
          <strong>Business Email Domain Match</strong>
          <span>{domainNote}</span>
        </div>

        <div className="admin-modal-grid">
          <Kv label="Business email" value={v.business_email} testId="v-modal-email"/>
          <Kv label="Business phone" value={v.business_phone} testId="v-modal-phone"/>
          <Kv label="Country" value={v.country}/>
          <Kv label="City" value={v.city}/>
          <Kv label="Buyer type" value={v.buyer_type}/>
          <Kv label="Website" value={v.company_website || "—"}/>
          <Kv label="LinkedIn" value={v.linkedin || "—"} full/>
          <Kv label="Product categories" value={(v.product_categories || []).join(", ") || "—"} full testId="v-modal-categories"/>
          <Kv label="Sourcing markets" value={v.sourcing_markets || "—"} full/>
          <Kv label="Company description" value={v.company_description || "—"} full/>
        </div>

        <label className="admin-modal-notes" data-testid="verification-admin-notes-label">
          <span>Internal admin notes (never shown to the buyer)</span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            data-testid="verification-admin-notes"
            placeholder="Notes visible to admins only…"
          />
        </label>

        <footer className="admin-modal-foot">
          <button className="admin-btn" onClick={onClose} data-testid="verification-modal-close">Close</button>
          <div className="admin-modal-actions">
            <button className="admin-btn" disabled={busy || v.status === "more_info"} onClick={() => decide("request_more_info")} data-testid="verification-request-more-info">
              Request More Info
            </button>
            <button className="admin-btn reject" disabled={busy || v.status === "rejected"} onClick={() => decide("reject")} data-testid="verification-reject">
              <X size={13}/> Reject
            </button>
            <button className="admin-btn approve" disabled={busy || v.status === "verified"} onClick={() => decide("approve")} data-testid="verification-approve">
              <Check size={13}/> Approve
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Kv({ label, value, testId, full }) {
  return (
    <div className={`kv${full ? " full" : ""}`} data-testid={testId}>
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}

// ---------- Main console ----------

const TABS = [
  ["content", "Content Manager"],
  ["homepage-media", "Homepage Media"],
  ["overview", "Overview"],
  ["commercial", "Commercial"],
  ["applications", "Applications"],
  ["verifications", "Buyer Verification"],
  ["exhibitors", "Exhibitors"],
  ["users", "Users"],
  ["products", "Products"],
  ["exhibitions", "Exhibitions"],
  ["buyers", "Buyers"],
  ["inquiries", "Inquiries"],
  ["meetings", "Meetings"],
];

function Console({ user, onSignOut }) {
  const [tab, setTab] = useState("overview");
  const data = useAdminData();

  const handleApplicationStatus = async (id, status, details) => {
    try { await api.updateApplicationStatus(id, status, details); await data.refresh(); }
    catch (e) {
      if (status === "approved") throw e;
      alert(`Could not update application: ${e.message}`);
    }
  };
  const handleExhibitorStatus = async (id, status) => {
    try { await api.updateExhibitorStatus(id, status); data.refresh(); }
    catch (e) { alert(`Could not update exhibitor: ${e.message}`); }
  };
  const handleRoleChange = async (userId, payload) => {
    try { await api.updateUserRole(userId, payload); data.refresh(); }
    catch (e) { alert(`Could not update role: ${e.message}`); }
  };
  const handleDecide = async (id, action, notes) => {
    try {
      await api.decideVerification(id, { action, admin_notes: notes || null });
      data.refresh();
    } catch (e) { alert(`Could not update verification: ${e.message}`); }
  };

  return (
    <div className="admin-shell" data-testid="admin-shell">
      <aside className="admin-side">
        <Link className="brand" to="/" data-testid="admin-brand-link">
          <span className="brand-mark"><Diamond size={15}/></span>
          <span>CARATJEWEL <b>EXPO</b></span>
        </Link>
        <div className="eyebrow" style={{ marginTop: 40 }}>ADMIN CONSOLE</div>
        {user && (
          <div className="admin-user" data-testid="admin-current-user">
            <strong>{user.name}</strong>
            <span>{user.email}</span>
          </div>
        )}
        <nav>
          {TABS.map(([id, label]) => (
            <button
              key={id}
              className={tab === id ? "active" : ""}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
            >
              {label}
              {id === "applications" && data.summary?.applications_pending > 0 && (
                <span className="admin-badge">{data.summary.applications_pending}</span>
              )}
              {id === "verifications" && data.summary?.buyer_verifications_pending > 0 && (
                <span className="admin-badge" data-testid="verifications-pending-badge">{data.summary.buyer_verifications_pending}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="admin-side-footer">
          <button className="admin-linklike" onClick={data.refresh} data-testid="admin-refresh">
            <RefreshCw size={13}/> Refresh
          </button>
          <button className="admin-linklike" onClick={onSignOut} data-testid="admin-signout">
            <LogOut size={13}/> Sign out
          </button>
        </div>
      </aside>
      <main className="admin-main">
        <header className="admin-main-head">
          <div>
            <div className="eyebrow">{TABS.find(([id]) => id === tab)?.[1].toUpperCase()}</div>
            <h1 data-testid="admin-panel-title">{TABS.find(([id]) => id === tab)?.[1]}</h1>
          </div>
          {data.loading && <span className="admin-loading" data-testid="admin-loading">Loading…</span>}
          {data.error && <span className="admin-error" data-testid="admin-error">{data.error}</span>}
        </header>
        <section className="admin-content">
          {tab === "content" && <AdminContent exhibitions={data.exhibitions} exhibitors={data.exhibitors} onExhibitionSaved={data.refresh}/>}
          {tab === "homepage-media" && <HomepageMediaAdmin/>}
          {tab === "overview" && <OverviewTab data={data} onGoto={setTab}/>}
          {tab === "commercial" && <AdminCommercial applications={data.applications} exhibitors={data.exhibitors}/>}
          {tab === "applications" && <ApplicationsTab data={data} onStatus={handleApplicationStatus}/>}
          {tab === "verifications" && <VerificationsTab data={data} onDecide={handleDecide}/>}
          {tab === "exhibitors" && <ExhibitorsTab data={data} onStatus={handleExhibitorStatus}/>}
          {tab === "users" && <UsersTab data={data} exhibitors={data.exhibitors} onRoleChange={handleRoleChange}/>}
          {tab === "products" && <ProductsTab data={data}/>}
          {tab === "exhibitions" && <ExhibitionsTab data={data}/>}
          {tab === "buyers" && <BuyersTab data={data}/>}
          {tab === "inquiries" && <><InquiriesTab data={data}/><ReportSupport/></>}
          {tab === "meetings" && <MeetingsTab data={data}/>}
        </section>
      </main>
    </div>
  );
}

export default function Admin() {
  const { user, signOut } = useAuth();
  return (
    <>
      <style>{ADMIN_STYLES}</style>
      <Console user={user} onSignOut={signOut}/>
    </>
  );
}

const ADMIN_STYLES = `
.admin-gate{min-height:100vh;display:grid;place-items:center;background:#fbfbfa;padding:40px 20px}
.admin-gate-card{width:100%;max-width:420px;border:1px solid var(--line);background:#fff;padding:44px 38px}
.admin-gate-card h1{font:500 32px/1.1 'Manrope',sans-serif;letter-spacing:-.02em;margin:14px 0 12px}
.admin-gate-card p{font-size:12px;line-height:1.7;color:#777;margin:0 0 28px}
.admin-gate-card label{display:block;font:10px 'DM Mono',monospace;letter-spacing:.08em;color:#555}
.admin-gate-card input{display:block;width:100%;padding:14px 0;border:0;border-bottom:1px solid #aaa;background:transparent;outline:0;font-size:14px;margin-top:8px;letter-spacing:.2em}
.admin-gate-card .full-button{width:100%;margin-top:28px}
.admin-gate-card small{display:flex;align-items:center;gap:7px;font-size:10px;color:#999;margin-top:22px}
.admin-gate-error{font-size:11px;color:#b04a3a;margin-top:14px;font-family:'DM Mono',monospace}
.admin-shell{min-height:100vh;display:grid;grid-template-columns:250px 1fr;background:#fbfbfa}
.admin-side{border-right:1px solid var(--line);padding:32px 26px;display:flex;flex-direction:column;background:#fff;position:sticky;top:0;height:100vh}
.admin-side .brand{margin-bottom:4px}
.admin-side nav{display:flex;flex-direction:column;gap:2px;margin-top:18px;flex:1}
.admin-side nav button{background:none;border:0;text-align:left;padding:11px 12px;font-size:12px;color:#555;display:flex;justify-content:space-between;align-items:center;cursor:pointer;border-radius:2px}
.admin-side nav button:hover{background:#f2f2ef;color:var(--ink)}
.admin-side nav button.active{background:var(--ink);color:#fff}
.admin-badge{background:#c4664f;color:#fff;font:9px 'DM Mono',monospace;padding:2px 6px;border-radius:2px}
.admin-side nav button.active .admin-badge{background:#fff;color:var(--ink)}
.admin-side-footer{display:flex;gap:14px;padding-top:20px;border-top:1px solid var(--line)}
.admin-linklike{background:none;border:0;font-size:11px;color:#777;display:inline-flex;gap:6px;align-items:center;cursor:pointer;padding:0}
.admin-linklike:hover{color:var(--ink)}
.admin-main{padding:44px 6vw 90px}
.admin-main-head{display:flex;justify-content:space-between;align-items:end;margin-bottom:34px;gap:18px}
.admin-main-head h1{font:500 40px/1 'Manrope',sans-serif;margin:8px 0 0;letter-spacing:-.02em}
.admin-loading{font:10px 'DM Mono',monospace;color:#888}
.admin-error{font:10px 'DM Mono',monospace;color:#b04a3a}
.admin-metrics{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:34px}
.admin-metric{border:1px solid var(--line);padding:18px 20px;background:#fff}
.admin-metric span{display:block;font:9px 'DM Mono',monospace;color:#888;letter-spacing:.06em}
.admin-metric strong{display:block;font:34px 'Playfair Display',serif;font-weight:500;margin-top:8px}
.admin-overview-panels{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.admin-panel{background:#fff;border:1px solid var(--line);padding:24px;display:flex;justify-content:space-between;align-items:center;text-align:left;cursor:pointer}
.admin-panel:hover{border-color:var(--ink)}
.admin-panel h3{font:500 18px/1.2 'Manrope',sans-serif;margin:8px 0 0}
.admin-panel strong{font:32px 'Playfair Display',serif;font-weight:500}
.admin-table{background:#fff;border:1px solid var(--line)}
.admin-table-head,.admin-row{display:grid;grid-template-columns:1.5fr 1.4fr 1fr .8fr .8fr 1.4fr;gap:12px;padding:14px 20px;font-size:12px;align-items:center}
.admin-table-head.five-col,.admin-row.five-col{grid-template-columns:1.6fr 1.6fr 1.4fr 1.4fr 1fr}
.admin-table-head.six-col,.admin-row.six-col{grid-template-columns:1.4fr 1.6fr .8fr 1.2fr 1fr 1.6fr}
.admin-user{margin-top:14px;padding:12px 12px;border:1px solid var(--line);background:#fbfbfa}
.admin-user strong{display:block;font-size:12px}
.admin-user span{display:block;font-size:10px;color:#888;margin-top:3px;word-break:break-all}
.admin-role-controls{display:flex;flex-direction:column;gap:6px}
.admin-role-select{border:1px solid var(--line);background:#fff;padding:6px 8px;font-size:11px;font-family:inherit}
.admin-table-head{background:#f4f4f0;font:10px 'DM Mono',monospace;color:#666;letter-spacing:.06em;text-transform:uppercase}
.admin-row{border-top:1px solid var(--line)}
.admin-row .muted,.admin-row span.muted{color:#888;font-size:11px}
.admin-empty{padding:32px 20px;text-align:center;color:#888;font-size:12px;border-top:1px solid var(--line)}
.admin-pill{font:9px 'DM Mono',monospace;padding:5px 8px;border:1px solid var(--line);letter-spacing:.05em;text-transform:uppercase;white-space:nowrap}
.admin-pill.tone-ok{border-color:#8ba695;color:#4c6c56;background:#eef4ef}
.admin-pill.tone-warn{border-color:#c9a06f;color:#7c5729;background:#f7efe0}
.admin-pill.tone-danger{border-color:#c98d84;color:#8a3f34;background:#f6e4e0}
.admin-pill.tone-info{border-color:#8ea4b3;color:#3e5a70;background:#e9eef4}
.admin-pill.tone-muted{border-color:var(--line);color:#666;background:#f4f4f0}
.admin-actions{display:flex;gap:6px}
.admin-btn{border:1px solid var(--line);background:#fff;padding:6px 10px;font-size:11px;display:inline-flex;align-items:center;gap:6px;cursor:pointer}
.admin-btn:hover:not(:disabled){border-color:var(--ink)}
.admin-btn:disabled{opacity:.35;cursor:not-allowed}
.admin-btn.approve:hover:not(:disabled){background:#eef4ef;color:#3f5b49}
.admin-btn.reject:hover:not(:disabled){background:#f6e4e0;color:#7a382e}
.verify-filter-bar{display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap}
.verify-filter{background:transparent;border:1px solid var(--line);padding:8px 14px;font:11px 'Manrope',sans-serif;color:#555;cursor:pointer;font-family:inherit}
.verify-filter:hover{border-color:var(--ink)}
.verify-filter.on{background:var(--ink);color:#fff;border-color:var(--ink)}
.admin-table-head.verify-cols,.admin-row.verify-cols{grid-template-columns:1.6fr 1.4fr .8fr 1.3fr .9fr .9fr .8fr}
.admin-modal-backdrop{position:fixed;inset:0;background:rgba(15,20,20,.55);display:grid;place-items:center;padding:32px 16px;z-index:200}
.admin-modal{background:#fff;max-width:720px;width:100%;max-height:88vh;overflow:auto;border:1px solid var(--ink);padding:34px 36px;display:flex;flex-direction:column;gap:20px}
.admin-modal-head{display:flex;justify-content:space-between;gap:20px;align-items:start;border-bottom:1px solid var(--line);padding-bottom:16px}
.admin-modal-head h2{font:500 26px 'Manrope',sans-serif;margin:6px 0 4px;letter-spacing:-.01em}
.admin-modal-sub{font-size:12px;color:#777;margin:0}
.admin-modal-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px 22px}
.kv{display:flex;flex-direction:column;gap:4px}
.kv.full{grid-column:1/-1}
.kv span{font:9px 'DM Mono',monospace;letter-spacing:.08em;color:#888;text-transform:uppercase}
.kv strong{font-size:13px;font-weight:500;color:var(--ink);word-break:break-word;line-height:1.5}
.verify-flag{border:1px solid var(--line);padding:12px 16px;display:flex;flex-direction:column;gap:4px;background:#fbfbfa}
.verify-flag strong{font:10px 'DM Mono',monospace;letter-spacing:.08em;color:#555}
.verify-flag span{font-size:12px;color:#555;line-height:1.6}
.verify-flag-ok{border-color:#8ba695;background:#eef4ef}.verify-flag-ok strong{color:#3f5b49}
.verify-flag-warn{border-color:#c9a06f;background:#f7efe0}.verify-flag-warn strong{color:#7c5729}
.admin-modal-notes{display:flex;flex-direction:column;gap:6px}
.admin-modal-notes span{font:9px 'DM Mono',monospace;color:#888;letter-spacing:.06em;text-transform:uppercase}
.admin-modal-notes textarea{border:1px solid var(--line);padding:10px 12px;font-size:13px;font-family:inherit;resize:vertical;min-height:60px}
.admin-modal-foot{display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap;border-top:1px solid var(--line);padding-top:16px}
.admin-modal-actions{display:flex;gap:8px;flex-wrap:wrap}
.application-admin-actions{display:flex!important;flex-wrap:wrap;align-items:center;gap:6px}.application-review-modal{max-width:780px}.application-review-context{display:grid;grid-template-columns:1.35fr .65fr;gap:1px;background:var(--line);border:1px solid var(--line)}.application-review-context>div{background:#f8f5f9;padding:16px 18px}.application-review-context span,.application-review-proof span{display:block;font:9px 'DM Mono',monospace;letter-spacing:.08em;text-transform:uppercase;color:#846d8e;margin-bottom:7px}.application-review-context strong{font-size:13px;font-weight:600;line-height:1.5}.application-review-section{border-top:1px solid var(--line);padding-top:18px}.application-review-section h3{font:600 12px 'Manrope',sans-serif;letter-spacing:.03em;margin:0 0 16px}.application-review-section .kv strong a{color:#654c70;text-decoration:underline;text-underline-offset:3px}.application-review-proof{border:1px solid var(--line);padding:15px 17px;display:flex;align-items:center;justify-content:space-between;gap:20px;background:#fbfbfa}.application-review-proof.ready{border-color:#abc1b2;background:#f2f7f3}.application-review-proof strong,.application-review-proof small{display:block}.application-review-proof strong{font-size:12px}.application-review-proof small{font-size:10px;color:#777;margin-top:5px}
@media(max-width:1000px){
  .admin-shell{grid-template-columns:1fr}
  .admin-side{position:relative;height:auto;padding:20px 6vw 16px;flex-direction:column;align-items:stretch}
  .admin-side nav{flex-direction:row;flex-wrap:wrap;margin-top:16px;gap:6px}
  .admin-side nav button{padding:9px 12px;gap:8px}
  .admin-side nav button .admin-badge{margin-left:2px}
  .admin-side-footer{border-top:1px solid var(--line);padding-top:14px;margin-top:14px}
  .admin-main{padding:32px 6vw 60px}
  .admin-metrics{grid-template-columns:repeat(2,1fr)}
  .admin-overview-panels{grid-template-columns:1fr}
  .admin-table-head{display:none}
  .admin-row,.admin-row.five-col,.admin-row.six-col{grid-template-columns:1fr;gap:8px;padding:18px 18px;position:relative}
  .admin-row>span,.admin-row.five-col>span,.admin-row.six-col>span{display:flex;justify-content:space-between;gap:14px;font-size:12px}
  .admin-row>span:before,.admin-row.five-col>span:before{content:attr(data-label);font:9px 'DM Mono',monospace;color:#888;text-transform:uppercase;letter-spacing:.06em}
  .admin-actions{justify-content:flex-end}
  .application-review-context{grid-template-columns:1fr}.application-admin-actions{justify-content:flex-end!important}.application-review-proof{align-items:flex-start;flex-direction:column}
}`;
